import ChevronRightIcon from '@material-design-icons/svg/filled/chevron_right.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import StarIcon from '@material-design-icons/svg/filled/star.svg?react';
import { isRight } from 'fp-ts/Either';
import { type SyntheticEvent, use, useState } from 'react';
import { toast } from 'sonner';
import { TRPCContext } from '../../contexts/TRPC';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { getDappPathBySegment } from '../../utils/dappRoutes';
import { getErrorMessage } from '../../utils/errorHandling';
import { StatusPill, type StatusVariant } from './primitives';
import {
  formatDateTime,
  formatEmailStatus,
  formatWindow,
  getEmailStatusVariant,
} from './runFormatting';
import { Button } from './uiButton';

type RunDetailResult = RightResult<
  RouterOutputs['getDappReviewSummaryRunDetail']
>;
type AppSummary = RunDetailResult['summaries'][number];

type InputReviewsResult = RightResult<
  RouterOutputs['getDappReviewSummaryInputReviews']
>;

interface Props {
  summary: AppSummary;
  variant: StatusVariant;
  statusLabel: string;
}

export function DappReviewSummaryAppCard({
  summary,
  variant,
  statusLabel,
}: Props) {
  const trpc = use(TRPCContext);
  const [expanded, setExpanded] = useState(false);
  const [reviews, setReviews] = useState<InputReviewsResult | null>(null);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'included'>(
    'included',
  );

  const loadReviews = async () => {
    if (!trpc?.client || loadingReviews) {
      return;
    }
    try {
      setLoadingReviews(true);
      setReviewsError(null);
      const result = await trpc.client.getDappReviewSummaryInputReviews.query({
        summaryId: summary.id,
      });

      if (isRight(result)) {
        setReviews(result.right);
      } else {
        setReviews(null);
        setReviewsError(result.left.message);
        toast.error(result.left.message);
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load input reviews');
      setReviewsError(message);
      toast.error(message);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleToggle = () => {
    setExpanded((current) => {
      const next = !current;
      if (
        next &&
        !reviews &&
        !loadingReviews &&
        summary.sourceReviewIds.length > 0
      ) {
        void loadReviews();
      }
      return next;
    });
  };

  const filteredReviews = reviews?.reviews.filter((review) =>
    reviewFilter === 'all' ? true : review.included,
  );

  const displayName = summary.dappName ?? summary.androidPackage;

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        className="group flex w-full items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-white/[0.02]"
        aria-expanded={expanded}
      >
        <ChevronRightIcon
          className={`h-4 w-4 shrink-0 fill-neutral-500 transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[14px] font-semibold text-white">
              {displayName}
            </span>
            <StatusPill variant={variant}>{statusLabel}</StatusPill>
            <StatusPill variant={getEmailStatusVariant(summary.emailStatus)}>
              Email: {formatEmailStatus(summary.emailStatus)}
            </StatusPill>
          </div>
          {summary.dappName && (
            <div className="mt-0.5 truncate">
              <CopyablePackageName value={summary.androidPackage} />
            </div>
          )}
        </div>

        <div className="hidden items-center gap-6 text-right md:flex">
          <Pair label="Fetched" value={summary.inputReviewCount} />
          <Pair
            label="Included"
            value={summary.includedReviewCount}
            tone={
              summary.includedReviewCount > 0 && variant === 'success'
                ? 'success'
                : undefined
            }
          />
          <Pair
            label="Truncated"
            value={summary.sourceTruncated ? 'Yes' : 'No'}
            tone={summary.sourceTruncated ? 'warning' : undefined}
          />
          <Pair
            label="Email"
            value={formatEmailStatus(summary.emailStatus)}
            tone={summary.emailStatus === 'Sent' ? 'success' : undefined}
          />
        </div>
      </button>

      {expanded && (
        <div className="space-y-5 border-t border-white/[0.04] bg-white/[0.01] px-6 pb-6 pt-5">
          <div>
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">
                Summary
              </div>
              <a
                href={getDappPathBySegment(summary.androidPackage, '/reviews')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1 text-[11px] font-medium text-neutral-300 transition-colors hover:bg-white/[0.06] hover:text-white"
              >
                <OpenInNewIcon className="size-3 fill-current" />
                Open reviews page
              </a>
            </div>
            {summary.summary ? (
              <blockquote className="mt-2 border-l-2 border-teal-400/30 pl-4 text-[14px] leading-7 text-neutral-100 whitespace-pre-wrap">
                {summary.summary}
              </blockquote>
            ) : (
              <p className="mt-2 text-[13px] text-neutral-500 italic">
                No summary text. Status: {statusLabel}.
              </p>
            )}

            {summary.errorMessage && (
              <div className="mt-3 rounded-lg border border-rose-400/20 bg-rose-400/10 px-3 py-2 text-sm text-rose-200">
                {summary.errorMessage}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-y border-white/[0.04] py-4 md:grid-cols-4">
            <Meta
              label="Window"
              value={formatWindow(summary.windowStart, summary.windowEnd)}
            />
            <Meta label="Updated" value={formatDateTime(summary.updated)} />
            <Meta label="Classifier" value={summary.classificationModel} mono />
            <Meta label="Summarizer" value={summary.summarizationModel} mono />
            <Meta
              label="Prompt version"
              value={String(summary.promptVersion)}
            />
            <Meta
              label="Email"
              value={buildEmailDetail(summary)}
              className="col-span-2"
            />
            {summary.emailId && (
              <Meta
                label="Email id"
                value={summary.emailId}
                mono
                className="col-span-2"
              />
            )}
            <Meta
              label="Summary id"
              value={summary.id}
              mono
              className="col-span-2 md:col-span-3"
            />
          </div>

          <div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">
                  Input reviews
                </div>
                <p className="mt-1 max-w-md text-[12px] text-neutral-500">
                  Reviews pulled for this window. The ones tagged{' '}
                  <span className="text-emerald-300">included</span> were
                  classified useful and passed to the summarizer.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {reviews && (
                  <div className="inline-flex items-center rounded-lg border border-white/[0.06] bg-white/[0.02] p-0.5 text-[12px]">
                    {(['included', 'all'] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setReviewFilter(value)}
                        className={`rounded-md px-2.5 py-1 transition-colors ${
                          reviewFilter === value
                            ? 'bg-white/[0.08] text-white'
                            : 'text-neutral-400 hover:text-white'
                        }`}
                      >
                        {value === 'included'
                          ? `Included · ${reviews.includedReviewCount}`
                          : `All · ${reviews.reviews.length}`}
                      </button>
                    ))}
                  </div>
                )}

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={loadReviews}
                  submitting={loadingReviews}
                  disabled={
                    loadingReviews || summary.sourceReviewIds.length === 0
                  }
                >
                  {reviews ? 'Reload' : 'Load reviews'}
                </Button>
              </div>
            </div>

            <div className="mt-4">
              {summary.sourceReviewIds.length === 0 ? (
                <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-4 py-3 text-[13px] text-neutral-500">
                  No reviews were used as input for this summary.
                </div>
              ) : reviewsError ? (
                <div className="rounded-lg border border-rose-400/20 bg-rose-400/10 px-3 py-2 text-sm text-rose-200">
                  {reviewsError}
                </div>
              ) : !reviews ? (
                <SourceIdPreview ids={summary.sourceReviewIds} />
              ) : filteredReviews && filteredReviews.length === 0 ? (
                <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-4 py-3 text-[13px] text-neutral-500">
                  No reviews to display with the current filter.
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {filteredReviews?.map((review) => (
                    <li
                      key={review.id}
                      className={`rounded-xl border p-3.5 transition-colors ${
                        review.included
                          ? 'border-emerald-400/15 bg-emerald-400/[0.04]'
                          : 'border-white/[0.05] bg-white/[0.01]'
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-neutral-500">
                        <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.04] px-1.5 py-0.5 text-amber-200">
                          <StarIcon className="size-3 fill-current" />
                          <span className="tabular-nums">{review.rating}</span>
                        </span>
                        <span>{formatDateTime(review.createdAt)}</span>
                        {review.included && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-400/10 px-1.5 py-0.5 font-medium text-emerald-200">
                            Included
                          </span>
                        )}
                        <span className="ml-auto font-mono text-[10px] text-neutral-600">
                          {review.id}
                        </span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-[13px] leading-6 text-neutral-100">
                        {review.review}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CopyablePackageName({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event: SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Package name copied', { duration: 1500 });
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Failed to copy package name');
    }
  };

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleCopy(event);
        }
      }}
      title={copied ? 'Copied!' : 'Click to copy'}
      className="inline-block max-w-full truncate rounded font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30"
    >
      {value}
    </span>
  );
}

function buildEmailDetail(summary: AppSummary) {
  const base = formatEmailStatus(summary.emailStatus);

  if (summary.emailStatus === 'Sent') {
    const recipient = summary.emailRecipientEmail
      ? ` to ${summary.emailRecipientEmail}`
      : '';
    const sentAt = summary.emailSentAt
      ? ` at ${formatDateTime(summary.emailSentAt)}`
      : '';
    return `${base}${recipient}${sentAt}`;
  }

  const reason =
    summary.emailSkippedReason || summary.emailErrorMessage || null;
  const recipient = summary.emailRecipientEmail
    ? ` Recipient: ${summary.emailRecipientEmail}.`
    : '';

  return reason ? `${base}. ${reason}${recipient}` : base;
}

function Pair({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: 'success' | 'warning';
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">
        {label}
      </div>
      <div
        className={`mt-0.5 text-[13px] font-semibold tabular-nums ${
          tone === 'success'
            ? 'text-emerald-200'
            : tone === 'warning'
              ? 'text-amber-200'
              : 'text-white'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
  className,
}: {
  label: string;
  value: string;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-neutral-500">
        {label}
      </div>
      <div
        className={`mt-0.5 text-[13px] text-neutral-200 break-words ${
          mono ? 'font-mono text-[11px] text-neutral-300' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SourceIdPreview({ ids }: { ids: readonly string[] }) {
  return (
    <div className="space-y-3 rounded-xl border border-white/[0.04] bg-white/[0.01] p-4">
      <div className="text-[12px] text-neutral-500">
        Source review IDs ({ids.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ids.slice(0, 40).map((id) => (
          <span
            key={id}
            className="rounded-md border border-white/[0.05] bg-white/[0.02] px-2 py-0.5 font-mono text-[11px] text-neutral-400"
          >
            {id}
          </span>
        ))}
        {ids.length > 40 && (
          <span className="rounded-md border border-white/[0.05] bg-white/[0.02] px-2 py-0.5 text-[11px] text-neutral-500">
            +{ids.length - 40} more
          </span>
        )}
      </div>
      <p className="text-[12px] text-neutral-500">
        Click "Load reviews" to fetch the review text live from the store.
      </p>
    </div>
  );
}
